import React, { useContext, useEffect, useState } from "react";
import FilterBox from "../common/FilterBox";
import UserDirectoryFilter from "./userDirectoryFilter/UserDirectoryFilter";
import { SearchBox } from "../common/SearchBox";
import {
  FilterDataType,
  IDepartmentData,
  IUserInfo,
  searchUsers,
  USER_TYPE,
} from "@home-health-notify/domain";
import {
  IOrganizationContext,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import FilterTooltipText from "./FilterTooltipText";
import SelectedUserFilters from "../common/SelectedUserFilters";

export const getFilterTooltipCount = (
  filterData: FilterDataType | undefined,
) => {
  if (!filterData) return 0;
  let count = 0;
  const userRoleLength = Array.isArray(filterData.userRole)
    ? filterData.userRole.length
    : 0;
  const userTypeLength = Array.isArray(filterData.userType)
    ? filterData.userType.length
    : 0;
  count += userRoleLength > 0 ? userRoleLength : 0;
  count += userTypeLength > 0 ? userTypeLength : 0;
  return count;
};

// Create the interface for required props for the UserDirectorySearch function
interface UserDirectorySearchProps {
  setFilteredUserDirectory: React.Dispatch<React.SetStateAction<IUserInfo[]>>;
  filterTooltipData: FilterDataType;
  setFilterTooltipData: React.Dispatch<React.SetStateAction<FilterDataType>>;
  currentUserTypes?: USER_TYPE[];
  defaultSelectedUserTypes?: USER_TYPE[];
}

function UserDirectorySearch({
  setFilteredUserDirectory,
  filterTooltipData,
  setFilterTooltipData,
  currentUserTypes = [],
  defaultSelectedUserTypes = [],
}: UserDirectorySearchProps) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "userDirectory" });
  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const loggedInUser = selectedOrgUser?.getOrganization().userProfile.type;
  const departments: IDepartmentData[] =
    selectedOrgUser?.getDepartments() ?? [];
  const shortHandDepartments = departments.map(
    (department: IDepartmentData) => department.shortHand,
  );

  const initialFilterData = {
    userRole: [],
    userType: defaultSelectedUserTypes,
  };

  const [searchText, setSearchText] = useState("");
  const [filterData, setFilterData] =
    useState<FilterDataType>(initialFilterData);

  const hideFilterBox =
    currentUserTypes.includes(USER_TYPE.PHYSICIAN) ||
    currentUserTypes.includes(USER_TYPE.PATIENT);

  const searchAndFilterUserDirectory = (
    filterData: FilterDataType,
    searchText: string,
  ) => {
    if (!selectedOrgUser) {
      setFilteredUserDirectory([]);
      return;
    }
    let filteredUsers =
      selectedOrgUser.userDirectory.filterUserDirectory(filterData);
    if (searchText.length !== 0) {
      filteredUsers = searchUsers(searchText, filteredUsers);
    }
    setFilteredUserDirectory(filteredUsers);
  };

  const onFilter = () => {
    /* This below condition is only applicable for field staff */
    if (loggedInUser === USER_TYPE.USER) {
      const updatedFilterData = {
        ...filterData,
        userType: filterData.userType.includes(USER_TYPE.ADMIN)
          ? [USER_TYPE.ADMIN]
          : defaultSelectedUserTypes,
      };
      const updatedFilterTooltipData = {
        ...filterTooltipData,
        userType: filterData.userType.includes(USER_TYPE.ADMIN)
          ? [USER_TYPE.ADMIN]
          : [],
      };
      searchAndFilterUserDirectory(updatedFilterData, searchText);
      setFilterData(updatedFilterData);
      setFilterTooltipData(updatedFilterTooltipData);
      return;
    }
    searchAndFilterUserDirectory(filterData, searchText);
    setFilterTooltipData(filterData);
  };

  useEffect(() => {
    const filterTooltipData = {
      ...initialFilterData,
      userType:
        loggedInUser === USER_TYPE.USER ? [] : initialFilterData.userType,
    };
    setFilterTooltipData(filterTooltipData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = () => {
    setFilterData(initialFilterData);
    const filterTooltipData = {
      ...initialFilterData,
      userType:
        loggedInUser === USER_TYPE.USER ? [] : initialFilterData.userType,
    };
    setFilterTooltipData(filterTooltipData);
    searchAndFilterUserDirectory(initialFilterData, "");
  };

  const onSearchTextChange = (text: string) => {
    setSearchText(text);
    searchAndFilterUserDirectory(filterData, text);
  };
  const onSearchClear = () => {
    setSearchText("");
    searchAndFilterUserDirectory(filterData, "");
  };

  return (
    <>
      <SearchBox
        placeholderLabel={tNs("searchPlaceholder")}
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
        onSearchClear={onSearchClear}
      >
        {!hideFilterBox && (
          <FilterBox
            title={tNs("filter.title")}
            onApply={onFilter}
            onReset={onReset}
            filterCount={getFilterTooltipCount(filterTooltipData)}
            filterText={
              <FilterTooltipText filterTooltipData={filterTooltipData} />
            }
          >
            <UserDirectoryFilter
              departments={shortHandDepartments}
              filterData={filterData}
              setFilterData={setFilterData}
              currentUserTypes={currentUserTypes}
            />
          </FilterBox>
        )}
      </SearchBox>
      <SelectedUserFilters
        filters={filterTooltipData}
        filterCount={getFilterTooltipCount(filterTooltipData)}
        defaultFilterCount={2}
      />
    </>
  );
}

export default UserDirectorySearch;
